import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { useTheme } from '@/services/theme';
import { breakpoints } from '@/shared/styles';
import { Typography } from '@/shared/components/Typography';
import { Accordion } from '@/shared/components/Accordion';
import { preloadImage } from '@/shared/utils/preload';

import Yield from './yield.svg';
import Curated from './curated.svg';
import NewYield from './new_yield.svg';
import Interop from './interop.svg';

const StyledFade = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  &.enter {
    opacity: 0;

    &-done {
      opacity: 1;
    }
  }

  &.exit {
    opacity: 0;
  }

  transition: opacity 250ms;
`;

const Container = styled.section`
  position: relative;
  text-align: center;
  background: rgba(24, 25, 31, 1);
  border-radius: 32px 32px 0 0;

  margin-top: 60px;
  padding: 40px 20px;

  @media (min-width: ${breakpoints.tabletSM}px) {
    border-radius: 64px 64px 0 0;
    padding: 100px 60px;
    margin-top: 180px;
  }
`;

const StyledContentMobile = styled.div`
  display: block;
  margin-top: 40px;

  @media (min-width: ${breakpoints.tabletSM}px) {
    display: none;
  }
`;

const StyledContentDesktop = styled.div`
  display: none;

  flex-direction: column;
  border: 1px solid #1f2028;
  border-radius: 38px;

  margin-top: 40px;

  @media (min-width: ${breakpoints.tabletSM}px) {
    display: flex;
    margin-top: 60px;
    border-radius: 38px;
    flex-direction: row;
    height: 445px;
  }

  .Vaults__image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    & > img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      object-position: center;
    }

    & > svg {
      width: 100%;
      height: 100%;
    }
  }

  .Vaults__sections {
    display: flex;
    flex-direction: column;

    border-radius: 0 0px 20px 20px;
    overflow: hidden;

    max-width: 300px;

    @media (min-width: ${breakpoints.tabletSM}px) {
      border-radius: 0 38px 38px 0;
      width: 100%;
      border-left: 1px solid #1f2028;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      width: 100%;
    }

    @media (min-width: ${breakpoints.tabletXL}px) {
      max-width: 415px;
    }

    .Vaults__section {
      background: none;
      color: inherit;
      border: none;
      font: inherit;
      cursor: pointer;
      outline: inherit;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;

      padding: 20px 10px;

      @media (min-width: ${breakpoints.tabletSM}px) {
        padding: 30px 20px;
      }
    }
  }
`;

const VaultSectionTitle = styled(Typography)`
  font-size: 1.6rem;

  @media (min-width: ${breakpoints.tabletSM}px) {
    font-size: 2rem;
  }
`;

const AccordionContent = styled.div`
  & > img {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain;
    object-position: center;
  }

  & > svg {
    height: 300px;
    width: 100%;
  }
`;

const sections = [
  {
    title: 'Commoditized Onchain Yield',
    image: <img loading="eager" src={Yield} alt="Commoditized Onchain Yield" />,
  },
  {
    title: 'Curated Model',
    image: <img src={Curated} alt="Curated Model" />,
  },
  {
    title: 'New Yield Sources',
    image: <img src={NewYield} alt="New Yield Sources" />,
  },
  {
    title: 'L2 Vaults',
    image: <img src={Interop} alt="L2 Vaults" style={{ padding: '0 20px' }} />,
  },
];

const accordionItems = sections.map(section => ({
  title: (
    <VaultSectionTitle
      fontFamily="gramatika"
      weight={700}
      size={1.6}
      variant="body1"
      color="primary"
    >
      {section.title}
    </VaultSectionTitle>
  ),
  panel: <AccordionContent>{section.image}</AccordionContent>,
}));

export const Vaults = () => {
  const { theme } = useTheme();
  const transitionRef = useRef();
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);

  const activeSection = sections[activeSectionIdx]!;

  // Preload images for Accordion / Image transition
  useEffect(() => {
    Promise.all([Yield, Curated, NewYield, Interop].map(src => preloadImage(src).catch(() => {})));
  }, []);

  return (
    <Container>
      <h2>Mellow Vault</h2>
      <div className="Content__description2">
        Access all flavors of onchain yield, on your own terms.
      </div>

      <StyledContentMobile>
        <Accordion
          backgroundColor="rgba(24, 25, 31, 1)"
          items={accordionItems}
          defaultActiveIndex={0}
        />
      </StyledContentMobile>

      <StyledContentDesktop theme={theme} className="Vaults__container">
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={activeSectionIdx}
            timeout={{ enter: 0, exit: 250 }}
            nodeRef={transitionRef}
          >
            <StyledFade ref={transitionRef}>
              <div className="Vaults__image">{activeSection.image}</div>
            </StyledFade>
          </CSSTransition>
        </SwitchTransition>

        <div className="Vaults__sections">
          {sections.map((section, index) => (
            <button
              key={index}
              type="button"
              className="Vaults__section"
              onMouseEnter={() => setActiveSectionIdx(index)}
              style={{
                background: index === activeSectionIdx ? 'rgba(237, 25, 67, 1)' : '',
                borderTop: index > 0 ? '1px solid #1f2028' : '',
              }}
            >
              <VaultSectionTitle
                fontFamily="gramatika"
                weight={700}
                size={1.6}
                variant="body1"
                color="primary"
              >
                {section.title}
              </VaultSectionTitle>
            </button>
          ))}
        </div>
      </StyledContentDesktop>
    </Container>
  );
};
